import * as Api from '@ViewModels';
import { getDisplayName } from '../../../../../models/NameUtils';
import { canScheduleEmailExec } from './scheduleUtils';

/** Colleagues beyond the owner, mirroring `EmailExecMailboxSourceService.MaxSourceMailboxes`. */
export const MAX_SOURCE_MAILBOXES = 10;

export const UNAVAILABLE_MAILBOX_LABEL = 'Unavailable user';

type MailboxScope = 'self' | 'all' | 'named';

interface MailboxSelection {
	scope: MailboxScope;
	userIds: string[];
}

export const SELF_ONLY_SELECTION: MailboxSelection = { scope: 'self', userIds: [] };

export const isEligibleMailboxUser = (user: Api.IUser, currentUserId: string | undefined): boolean =>
	!!user.id &&
	user.id !== currentUserId &&
	user.activationStatus === Api.ActivationStatus.ACTIVE &&
	canScheduleEmailExec(user) &&
	!user.userPreferences?.emailConnectionOptOut;

export const templateToMailboxSelection = (
	template: Api.EmailExecAssistantTemplate | null | undefined
): MailboxSelection => {
	const sources = template?.mailboxSources;
	if (sources?.includeAllUsers) {
		return { scope: 'all', userIds: [] };
	}
	const userIds = (sources?.userIds ?? []).filter(Boolean);
	return userIds.length > 0 ? { scope: 'named', userIds } : SELF_ONLY_SELECTION;
};

export const sameMailboxSelection = (a: MailboxSelection, b: MailboxSelection): boolean =>
	a.scope === b.scope &&
	a.userIds.length === b.userIds.length &&
	a.userIds.every((id, index) => id === b.userIds[index]);

export const mailboxAddress = (user: Api.IUser): string | undefined => user.email;

export const mailboxDisplayName = (user: Api.IUser): string => getDisplayName(user) || UNAVAILABLE_MAILBOX_LABEL;

export const mailboxSearchText = (user: Api.IUser): string => {
	const name = mailboxDisplayName(user);
	const address = mailboxAddress(user);
	return address ? `${name} ${address}` : name;
};

export const eligibleMailboxUsers = (users: Api.IUser[], currentUserId: string | undefined): Api.IUser[] =>
	users.filter(user => isEligibleMailboxUser(user, currentUserId));

export const selectedMailboxUsers = (users: Api.IUser[], selectedIds: string[]): Api.IUser[] =>
	selectedIds.map(id => users.find(user => user.id === id) ?? { id });

export const mailboxPickerItems = (
	users: Api.IUser[],
	selectedIds: string[],
	currentUserId: string | undefined
): Api.IUser[] => {
	const items = eligibleMailboxUsers(users, currentUserId);
	const present = new Set(items.map(user => user.id));
	for (const user of selectedMailboxUsers(users, selectedIds)) {
		if (!present.has(user.id)) {
			present.add(user.id);
			items.push(user);
		}
	}
	return items;
};

export const mailboxSourcesForSave = ({
	selection,
	stored,
	canConfigure,
}: {
	selection: MailboxSelection;
	stored: MailboxSelection;
	canConfigure: boolean;
}): Pick<Api.EmailExecAssistantTemplate, 'mailboxSources'> | Record<string, never> => {
	if (!canConfigure || sameMailboxSelection(selection, stored)) {
		return {};
	}
	if (selection.scope === 'all') {
		return { mailboxSources: { includeAllUsers: true } };
	}
	if (selection.scope === 'named' && selection.userIds.length > 0) {
		return { mailboxSources: { includeAllUsers: false, userIds: selection.userIds } };
	}
	return { mailboxSources: { includeAllUsers: false, userIds: [] } };
};

export const isMailboxSelectionValid = (selection: MailboxSelection): boolean =>
	selection.scope !== 'named' || (selection.userIds.length > 0 && selection.userIds.length <= MAX_SOURCE_MAILBOXES);
